"use client";

import { useState, type MouseEvent as ReactMouseEvent } from "react";
import cnBind from "classnames/bind";
import { useTranslation } from "react-i18next";

import type { NavbarItem } from "@/components/NavBar/constants";
import { ArrowDownIcon } from "@/shared/assests/svg/svg";

import styles from "./Navbar.module.scss";

const cx = cnBind.bind(styles);

const MobileLinkContent = ({ item, compact }: { item: NavbarItem; compact?: boolean }) => (
    <span className={cx("mobile-link-text")}>
        <span className={cx("link-label")}>{item.label}</span>
        {!compact && item.description ? <span className={cx("link-description")}>{item.description}</span> : null}
    </span>
);

type MobileMenuListProps = {
    items: NavbarItem[];
    onClick?: () => void;
    level?: number;
};

const MobileMenuList = ({ items, onClick, level = 0 }: MobileMenuListProps) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const toggleIndex = (event: ReactMouseEvent, index: number) => {
        event.preventDefault();
        event.stopPropagation();
        setOpenIndex((prev) => (prev === index ? null : index));
    };

    return (
        <div className={cx(level === 0 ? "items-mob" : "subitems-mob")} role={level === 0 ? "list" : "group"}>
            {items.map((item, i) => {
                const isOpen = openIndex === i;
                const hasChildren = Boolean(item.items?.length);

                return (
                    <div
                        key={`${item.label}-${i}`}
                        className={cx(level === 0 ? "item-mob" : "subitem-mob", isOpen && "item-mob-open")}
                        role="listitem"
                    >
                        {hasChildren ? (
                            <button
                                type="button"
                                className={cx(
                                    "link-mob",
                                    level === 0 ? "link-icon-mob" : "submenu-mob",
                                    isOpen && "link-active",
                                )}
                                aria-expanded={isOpen}
                                onClick={(event) => toggleIndex(event, i)}
                            >
                                <MobileLinkContent item={item} compact={level > 0} />
                                <ArrowDownIcon className={cx("icon-mob", isOpen && "icon-open")} />
                            </button>
                        ) : item.url ? (
                            <a
                                className={cx("link-mob", level === 0 && "link-icon-mob", level > 0 && "link-mob-leaf")}
                                onClick={onClick}
                                href={typeof item.url === "string" ? item.url : "#"}
                            >
                                <MobileLinkContent item={item} compact={level > 0} />
                            </a>
                        ) : (
                            <span className={cx("link-mob", "submenu-mob")}>
                                <MobileLinkContent item={item} compact={level > 0} />
                            </span>
                        )}

                        {hasChildren ? (
                            <div className={cx("subitems-wrapper-mob", isOpen && "open-mob")}>
                                <div className={cx("subitems-wrapper-mob-inner")}>
                                    <MobileMenuList items={item.items!} onClick={onClick} level={level + 1} />
                                </div>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};

export const NavBarMob = ({
    className,
    onClick,
    data,
}: {
    className?: string;
    classNameItems?: string;
    onClick?: () => void;
    data: NavbarItem[];
}) => {
    const { t } = useTranslation("common");
    return (
        <nav className={cx("navbar-mob", className)} aria-label={t("settings.navbar.mobileMenuAria")}>
            <MobileMenuList items={data} onClick={onClick} />
        </nav>
    );
};
